/**
 * Centralized Plan Configuration — Single Source of Truth
 *
 * Both web and mobile apps import from here via @garageregister/shared.
 * Firestore security rules must be manually kept in sync — see comments
 * in firestore.rules referencing this file.
 *
 * ANNOTATION GUIDE:
 * ─────────────────
 * Record limits:   max number of documents (Infinity = unlimited)
 * Storage limits:  in bytes (use MB / GB helpers below)
 * Feature flags:   boolean (true = enabled for this plan)
 *
 * Every module in the app MUST have an entry here. When adding a new
 * feature, add its limit/flag to PlanLimits and define values for
 * every tier.
 *
 * ADDING A NEW TIER:
 * ──────────────────
 * 1. Add the tier ID to PlanId type
 * 2. Add a PlanConfig entry to PLANS
 * 3. Update Firestore rules if the tier has different limits
 * 4. Update Stripe webhook to map the new product to the tier
 */

// ── Size helpers ────────────────────────────────────────────────

export const MB = 1024 * 1024;
export const GB = 1024 * MB;

// ── Types ───────────────────────────────────────────────────────

export type PlanId = "free" | "pro" | "partner";

export interface PlanLimits {
  // ── Record limits ─────────────────────────────────
  maxGarages: number;                    // garages per user
  maxVehiclesPerGarage: number;          // vehicles per garage
  maxServicesPerVehicle: number;         // service records per vehicle
  maxModsPerVehicle: number;             // mod records per vehicle
  maxProjectsPerVehicle: number;         // projects per vehicle
  maxTasksPerProject: number;            // tasks per project
  maxTrips: number;                      // trips per user (cross-vehicle)
  maxWheelSetsPerVehicle: number;        // wheel & tire sets per vehicle
  maxPerformanceLogsPerVehicle: number;  // performance logs per vehicle
  maxGloveboxItemsPerVehicle: number;    // glovebox documents per vehicle
  maxPhotosPerRecord: number;            // photos per service/mod/trip/etc.
  maxGalleryPhotosPerVehicle: number;    // standalone vehicle gallery photos (vehicle.photos[])
  maxWishlistItems: number;              // dream list items per user

  // ── Storage limits ────────────────────────────────
  maxPhotoSizeBytes: number;             // max single photo upload (bytes)
  maxDocumentSizeBytes: number;          // max single document upload (bytes)
  maxTotalStorageBytes: number;          // total storage quota per user (bytes)

  // ── Feature flags ─────────────────────────────────
  // true = enabled for this plan
  photoUploads: boolean;       // upload photos to records
  documentUploads: boolean;    // upload documents (glovebox, tasks, receipts)
  projects: boolean;           // create/manage projects & tasks
  trips: boolean;              // create/manage trips
  wheelSets: boolean;          // wheel & tire set tracking
  performanceLogs: boolean;    // dyno, drag, lap, sprint, roll race logs
  glovebox: boolean;           // document storage per vehicle
  publicProfile: boolean;      // public profile page (/@handle)
  publicShowcase: boolean;     // toggle vehicles public for showcase
  rideHandles: boolean;        // custom vehicle URLs (/r/handle)
  aiDocumentScan: boolean;     // Gemini AI "Snap & Log" scanning
  freeScansAllowed: number;    // lifetime free AI scans (Infinity for Pro)
  reminders: boolean;          // service reminders (future)
  exportData: boolean;         // CSV/PDF data export (future)
  analytics: boolean;          // vehicle analytics dashboard (future)
}

export interface PlanConfig {
  id: PlanId;
  name: string;            // display name ("Free", "Pro", "Partner")
  description: string;     // one-line description for UI
  limits: PlanLimits;
}

export interface PlanPricing {
  monthlyPrice: number;          // USD
  annualPrice: number;           // USD
  annualSavingsPercent: number;  // e.g. 37
}

// ── Plan Definitions ────────────────────────────────────────────

const FREE_LIMITS: PlanLimits = {
  // Record limits
  maxGarages: 1,
  maxVehiclesPerGarage: 3,           // past rides are exempt (see firestore.rules vehicles create)
  maxServicesPerVehicle: Infinity,   // services are always free (data goldmine per SPEC)
  maxModsPerVehicle: Infinity,       // mods are always free
  maxProjectsPerVehicle: 0,          // pro-only
  maxTasksPerProject: 0,             // pro-only
  maxTrips: 0,                       // pro-only
  maxWheelSetsPerVehicle: 0,         // pro-only
  maxPerformanceLogsPerVehicle: 0,   // pro-only
  maxGloveboxItemsPerVehicle: 3,     // paperwork hook (insurance / registration / title)
  maxPhotosPerRecord: 5,             // 5 photos per service/mod/trip on free
  maxGalleryPhotosPerVehicle: 3,     // 3 standalone gallery photos per vehicle on free
  maxWishlistItems: 5,               // limited dream list

  // Storage limits
  maxPhotoSizeBytes: 10 * MB,        // 10 MB per photo on free (Pro: 25 MB)
  maxDocumentSizeBytes: 15 * MB,     // 15 MB per doc on free (Pro: 50 MB)
  maxTotalStorageBytes: Infinity,    // dropped on free — count caps do the work

  // Feature flags
  photoUploads: true,
  documentUploads: true,
  projects: false,
  trips: false,
  wheelSets: false,
  performanceLogs: false,
  glovebox: true,
  publicProfile: true,
  publicShowcase: true,
  rideHandles: true,
  aiDocumentScan: true,         // free users can scan (quota-limited, not feature-gated)
  freeScansAllowed: 5,          // 5 lifetime free AI scans — configurable hook
  // Reminders on free cover ONLY glovebox expiry (insurance / registration /
  // emissions / inspection). Mileage / service-interval reminders are gated
  // separately in their feature surface (not shipped yet).
  reminders: true,
  exportData: false,
  analytics: false,
};

const PRO_LIMITS: PlanLimits = {
  // Record limits
  maxGarages: Infinity,
  maxVehiclesPerGarage: Infinity,
  maxServicesPerVehicle: Infinity,
  maxModsPerVehicle: Infinity,
  maxProjectsPerVehicle: Infinity,
  maxTasksPerProject: Infinity,
  maxTrips: Infinity,
  maxWheelSetsPerVehicle: Infinity,
  maxPerformanceLogsPerVehicle: Infinity,
  maxGloveboxItemsPerVehicle: Infinity,
  maxPhotosPerRecord: 20,            // 20 photos per record
  maxGalleryPhotosPerVehicle: Infinity,
  maxWishlistItems: Infinity,

  // Storage limits
  maxPhotoSizeBytes: 25 * MB,        // 25 MB per photo
  maxDocumentSizeBytes: 50 * MB,     // 50 MB per document
  maxTotalStorageBytes: 10 * GB,     // 10 GB total per user

  // Feature flags
  photoUploads: true,
  documentUploads: true,
  projects: true,
  trips: true,
  wheelSets: true,
  performanceLogs: true,
  glovebox: true,
  publicProfile: true,
  publicShowcase: true,
  rideHandles: true,
  aiDocumentScan: true,
  freeScansAllowed: Infinity,   // unlimited for Pro
  reminders: true,
  exportData: true,
  analytics: true,
};

export const PLANS: Record<PlanId, PlanConfig> = {
  free: {
    id: "free",
    name: "Free",
    description: "Basic vehicle tracking for casual enthusiasts",
    limits: FREE_LIMITS,
  },
  pro: {
    id: "pro",
    name: "Pro",
    description: "Unlimited everything for serious gearheads",
    limits: PRO_LIMITS,
  },
  partner: {
    id: "partner",
    name: "Partner",
    description: "Courtesy Pro access for partners and team",
    limits: { ...PRO_LIMITS },  // same as pro
  },
};

// ── Helpers ─────────────────────────────────────────────────────

/** Get the plan config for a given plan ID. Defaults to free. */
export function getPlanConfig(planId?: string): PlanConfig {
  if (planId && planId in PLANS) {
    return PLANS[planId as PlanId];
  }
  return PLANS.free;
}

/** Check if a plan ID grants pro-level access */
export function isProPlan(planId?: string): boolean {
  return planId === "pro" || planId === "partner";
}

// ── Plan limit errors ───────────────────────────────────────────

/**
 * Thrown by shared lib functions when a client-side write would exceed
 * the user's plan limits. Callers should render a paywall / upgrade CTA
 * rather than surface this as a generic error. Firestore rules enforce
 * the same caps as a backstop.
 */
export class PlanLimitError extends Error {
  readonly code: string;
  readonly limit: number;
  readonly current: number;
  constructor(code: string, message: string, limit: number, current: number) {
    super(message);
    this.name = "PlanLimitError";
    this.code = code;
    this.limit = limit;
    this.current = current;
  }
}

// ── Pricing ─────────────────────────────────────────────────────

export const PRO_PRICING: PlanPricing = {
  monthlyPrice: 6.99,
  annualPrice: 59.99,
  annualSavingsPercent: 37,
};

/** Effective monthly price when billed annually */
export const PRO_ANNUAL_MONTHLY_EFFECTIVE = +(
  PRO_PRICING.annualPrice / 12
).toFixed(2); // ~$5.00

// ── Feature Descriptions (for upgrade page) ─────────────────────

export const PRO_FEATURES = [
  { label: "Unlimited AI document scanning", description: "Registrations, insurance cards, dyno sheets, time slips, service receipts — AI extracts everything automatically (free tier gets 5 lifetime scans)", highlight: true },
  { label: "Unlimited photo gallery & per-record photos", description: "20 photos per service/mod/trip and an unlimited vehicle gallery — with 12 garage-themed filters, crop, and zoom (free: 5/record, 3/gallery)", highlight: true },
  { label: "Unlimited glovebox", description: "Keep every doc you own — titles, receipts, invoices, warranties (free: 3 docs per ride)" },
  { label: "Unlimited garages & vehicles", description: "No cap on garages or vehicles per garage (free: 1 garage, 3 vehicles)" },
  { label: "Unlimited public rides", description: "Make every ride a billboard with its own /r/ URL (free: all 3 free vehicles eligible)" },
  { label: "Performance logs", description: "Dyno runs, drag strip times, lap times, 0-60 sprints, and roll races", highlight: true },
  { label: "Wheels & tires", description: "Track wheel sets, tire specs, and pressure across multiple setups" },
  { label: "Projects with task lists", description: "Track builds with task lists, parts, timelines, and collaborators" },
  { label: "Trips with multi-stop legs", description: "Plan drives with day legs, lunch stops, and overnight stays. Log mileage and capture photos along the way" },
  { label: "Meet check-ins", description: "Check in at car meets to write the meet to your vehicle's history and put your ride on the live roster" },
  { label: "Service-interval reminders", description: "Mileage and time-based reminders for oil changes, inspections, and more (free tier already gets glovebox expiry reminders)" },
  { label: "Larger uploads", description: "25 MB photos and 50 MB documents (free: 10 MB / 15 MB)" },
  { label: "Data export", description: "Download your full vehicle history as CSV or PDF whenever you want" },
];
